import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'
import ElementPlus from 'element-plus'
import { registerSW } from 'virtual:pwa-register'
import App from './App.vue'
import { i18n } from './i18n'
import { router } from './router'
import { useAppStore } from './stores/app'
import { useLiveStore } from './stores/live'
import './styles.css'

if (import.meta.env.DEV) void import('element-plus/dist/index.css')

const SW_UPDATE_INTERVAL_MS = 60 * 60 * 1000

let lastSWUpdateCheckAt = 0
let swUpdateCheck: Promise<void> | undefined

function checkForSWUpdate(
  registration: ServiceWorkerRegistration,
  force = false,
): void {
  const now = Date.now()
  if (!force && now - lastSWUpdateCheckAt < SW_UPDATE_INTERVAL_MS) return
  if (swUpdateCheck) return

  lastSWUpdateCheckAt = now
  swUpdateCheck = registration.update()
    .then(() => undefined)
    .catch(() => {
      // A failed background check must not interrupt the current record.
    })
    .finally(() => {
      swUpdateCheck = undefined
    })
}

const app = createApp(App)
const pinia = createPinia()
app.use(pinia)
app.use(router)
app.use(i18n)
app.use(ElementPlus)
app.mount('#app')

const appStore = useAppStore(pinia)
const liveStore = useLiveStore(pinia)

const seoKeyByRoute: Readonly<Record<string, { title: string; description: string; keywords: string }>> = {
  live: { title: 'seo.liveTitle', description: 'seo.liveDescription', keywords: 'seo.liveKeywords' },
  calibration: { title: 'seo.calibrationTitle', description: 'seo.calibrationDescription', keywords: 'seo.calibrationKeywords' },
  history: { title: 'seo.historyTitle', description: 'seo.historyDescription', keywords: 'seo.historyKeywords' },
  rank: { title: 'seo.rankTitle', description: 'seo.rankDescription', keywords: 'seo.rankKeywords' },
  settings: { title: 'seo.settingsTitle', description: 'seo.settingsDescription', keywords: 'seo.settingsKeywords' },
  about: { title: 'seo.aboutTitle', description: 'seo.aboutDescription', keywords: 'seo.aboutKeywords' },
}

function setMeta(selector: string, attribute: string, value: string): void {
  document.querySelector<HTMLMetaElement>(selector)?.setAttribute(attribute, value)
}

function setCanonicalUrl(value: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'canonical'
    document.head.append(link)
  }
  link.href = value
}

function updateDocumentMetadata(): void {
  i18n.global.locale.value = appStore.locale
  document.documentElement.lang = appStore.locale

  const routeName = typeof router.currentRoute.value.name === 'string' ? router.currentRoute.value.name : 'live'
  const keys = seoKeyByRoute[routeName] ?? { title: 'seo.defaultTitle', description: 'seo.defaultDescription', keywords: 'seo.defaultKeywords' }
  const title = String(i18n.global.t(keys.title))
  const description = String(i18n.global.t(keys.description))
  const keywords = String(i18n.global.t(keys.keywords))
  const canonicalUrl = new URL(router.currentRoute.value.path || '/', window.location.origin).href
  const shareImageUrl = new URL('/icon-512.png', window.location.origin).href

  document.title = title
  setMeta('meta[name="description"]', 'content', description)
  setMeta('meta[name="keywords"]', 'content', keywords)
  setMeta('meta[property="og:title"]', 'content', title)
  setMeta('meta[property="og:description"]', 'content', description)
  setMeta('meta[property="og:url"]', 'content', canonicalUrl)
  setMeta('meta[property="og:image"]', 'content', shareImageUrl)
  setMeta('meta[property="og:locale"]', 'content', appStore.locale === 'zh-CN' ? 'zh_CN' : 'en_US')
  setMeta('meta[name="twitter:title"]', 'content', title)
  setMeta('meta[name="twitter:description"]', 'content', description)
  setMeta('meta[name="twitter:image"]', 'content', shareImageUrl)
  setCanonicalUrl(canonicalUrl)
}

watch([
  () => appStore.locale,
  () => router.currentRoute.value.name,
], updateDocumentMetadata, { immediate: true })

let updateSW: (reloadPage?: boolean) => Promise<void> = async () => {}
updateSW = registerSW({
  immediate: true,
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return

    checkForSWUpdate(registration, true)
    window.setInterval(
      () => checkForSWUpdate(registration, true),
      SW_UPDATE_INTERVAL_MS,
    )

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkForSWUpdate(registration)
      }
    })
    window.addEventListener('online', () => checkForSWUpdate(registration, true))
  },
  onNeedRefresh() {
    queueMicrotask(() => {
      const isActive = () => Boolean(liveStore.session && liveStore.session.status !== 'ended')
      if (!isActive()) {
        void updateSW(true)
        return
      }
      const stop = watch(() => liveStore.session?.status, () => {
        if (isActive()) return
        stop()
        void updateSW(true)
      })
    })
  },
})
